/**
 * TypedAnswer.jsx
 *
 * Text input for Hard Mode — player types the college name.
 * Includes auto-focus, enter-to-submit, and visual feedback.
 *
 * Submission flow:
 * 1. Player types college name
 * 2. Presses Enter or clicks Submit
 * 3. Parent runs fuzzy matching
 * 4. Component shows correct/incorrect feedback
 * 5. Lock prevents re-submission
 */

import React, { useState, useRef, useEffect } from 'react';
import { suggest } from '../utils/gamexMatch';
import nflCsvUrl from '../data/players_with_photos.csv';
import nbaCsvUrl from '../data/nbaplayers_with_photos.csv';
import moshCsvUrl from '../data/notablealumni_with_photos.csv';

// College pick list universe, built once per page load and shared by every
// TypedAnswer instance. Same self-contained pattern as GameX's name universe:
// fetch the bundled CSVs, take the college column, dedupe. An assist only -
// the game works identically if this never loads.
let collegeUniverseCache = null;
async function loadCollegeUniverse() {
  if (collegeUniverseCache) return collegeUniverseCache;
  try {
    const texts = await Promise.all(
      [nflCsvUrl, nbaCsvUrl, moshCsvUrl].map(async (url) => {
        const res = await fetch(url);
        return res.text();
      })
    );
    const colleges = new Set();
    for (const text of texts) {
      const lines = text.trim().split('\n').slice(1);
      for (const line of lines) {
        const college = line.split(',')[1]?.trim();
        if (college) colleges.add(college);
      }
    }
    collegeUniverseCache = Array.from(colleges).sort();
  } catch (e) {
    collegeUniverseCache = [];
  }
  return collegeUniverseCache;
}

const TypedAnswer = ({ onSubmit, correctAnswer, locked, feedback, questionKey }) => {
  const [value, setValue] = useState('');
  const [universe, setUniverse] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    let alive = true;
    loadCollegeUniverse().then((u) => { if (alive) setUniverse(u); });
    return () => { alive = false; };
  }, []);

  // Auto-focus and reset on new question
  useEffect(() => {
    setValue('');
    setShowSuggestions(false);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [questionKey]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (locked || !value.trim()) return;
    setShowSuggestions(false);
    onSubmit(value.trim());
  };

  // Spelling assist only, mirroring the Sr. pattern: picking a suggestion
  // FILLS the input, it never submits. Raw typed text always goes through the
  // parent's fuzzy match, so typing "pitt" or "ole miss" works exactly as
  // before the list existed.
  const suggestions = !locked && showSuggestions ? suggest(value, universe) : [];

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  return (
    <div className="typed-answer-container">
      <form onSubmit={handleSubmit} className="typed-answer-form">
        {/* display:flex keeps the input's flex:1 sizing working now that it
            has a positioning wrapper between it and the form */}
        <div style={{ position: 'relative', flex: 1, display: 'flex' }}>
        <input
          ref={inputRef}
          type="text"
          className={`typed-answer-input ${feedback === 'correct' ? 'correct' : ''} ${feedback === 'incorrect' ? 'incorrect' : ''}`}
          value={value}
          onChange={(e) => { setValue(e.target.value); setShowSuggestions(true); }}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
          placeholder="Type the college..."
          disabled={locked}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
        />
        {suggestions.length > 0 && (
          <ul className="gamex-suggestions">
            {suggestions.map((name) => (
              <li key={name}>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setValue(name);
                    setShowSuggestions(false);
                    if (inputRef.current) inputRef.current.focus();
                  }}
                >
                  {name}
                </button>
              </li>
            ))}
          </ul>
        )}
        </div>
        <button
          type="submit"
          className="typed-answer-submit"
          disabled={locked || !value.trim()}
        >
          Submit
        </button>
      </form>

      {locked && feedback && (
        <div className={`typed-answer-feedback ${feedback}`}>
          {feedback === 'correct' ? (
            <span>Correct!</span>
          ) : (
            <span>
              Incorrect — the answer was <strong>{correctAnswer}</strong>
            </span>
          )}
        </div>
      )}

      {locked && !feedback && (
        <div className="typed-answer-feedback incorrect">
          <span>
            Time's up! The answer was <strong>{correctAnswer}</strong>
          </span>
        </div>
      )}
    </div>
  );
};

export default TypedAnswer;
